import { NavLink } from 'react-router-dom'
import { useRoles, usePlatformAdminGrantSections } from '@/hooks/usePermission'
import { RequirePermission } from './RequirePermission'
import { useTenants } from '@/api/admin'
import { useAuth } from 'react-oidc-context'
import { useTheme } from '@/hooks/useTheme'
import type { SupportGrantSection } from '@/types'

interface Props {
  children: React.ReactNode
  activeTenantId: string | null
  onTenantChange: (id: string) => void
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      {label}
    </NavLink>
  )
}

function LockedNavItem({ label }: { label: string }) {
  return (
    <span
      className="nav-item nav-item-locked"
      title="Requires support grant from tenant-admin — Admin → Support Access"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
      {label}
    </span>
  )
}

export function Layout({ children, activeTenantId, onTenantChange }: Props) {
  const auth = useAuth()
  const roles = useRoles()
  const isAdmin = roles.includes('platform-admin')
  const { theme, toggle } = useTheme()

  const { data: tenantsData } = useTenants()
  const tenants = tenantsData?.data ?? []

  // For platform-admin-only users: set of sections currently unlocked by tenant-admin support grants.
  // null means the user is not platform-admin-only (bypass all grant checks).
  const grantedSections = usePlatformAdminGrantSections(activeTenantId)

  // Helper: returns true if the user can access a gated section
  function canAccess(section: SupportGrantSection): boolean {
    if (grantedSections === null) return true
    return grantedSections.has(section)
  }

  const username = auth.user?.profile.preferred_username ?? '?'
  const initial = username[0].toUpperCase()

  return (
    <div className="layout">
      <aside className="sidebar">
        {/* Logo */}
        <div className="sidebar-logo">
          <svg width="118" height="33" viewBox="0 0 200 56" xmlns="http://www.w3.org/2000/svg" aria-label="keel">
            <line className="logo-edge" x1="16" y1="8" x2="16" y2="48" strokeWidth="2.5" strokeLinecap="round"/>
            <line className="logo-edge" x1="16" y1="28" x2="38" y2="8" strokeWidth="2.5" strokeLinecap="round"/>
            <line className="logo-edge" x1="16" y1="28" x2="40" y2="48" strokeWidth="2.5" strokeLinecap="round"/>
            <line className="logo-dashed" x1="38" y1="8" x2="40" y2="48" strokeWidth="1" strokeDasharray="3,2"/>
            <circle className="logo-node" cx="16" cy="8" r="4"/>
            <circle className="logo-node" cx="16" cy="28" r="4"/>
            <circle className="logo-node" cx="16" cy="48" r="4"/>
            <circle className="logo-node" cx="38" cy="8" r="4"/>
            <circle className="logo-node" cx="40" cy="48" r="4"/>
            <text className="logo-wm" x="56" y="36" fontFamily="'Inter', 'Helvetica Neue', sans-serif" fontSize="28" fontWeight="500" letterSpacing="-0.5">keel</text>
            <line className="logo-ul" x1="56" y1="44" x2="128" y2="44" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>

        {/* Tenant selector */}
        {(tenants.length > 0 || isAdmin) && (
          <div className="tenant-selector">
            <div className="tenant-selector-label">Tenant</div>
            <select
              value={activeTenantId ?? ''}
              onChange={e => onTenantChange(e.target.value)}
            >
              <option value="" disabled>Select tenant…</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.slug}</option>
              ))}
            </select>
          </div>
        )}

        {/* Platform-admin support grant status banner */}
        {grantedSections !== null && activeTenantId && (
          grantedSections.size === 0 ? (
            <div className="support-grant-banner support-grant-banner--none">
              No support grants — some sections are locked.
              Ask tenant-admin: <strong>Admin → Support Access</strong>.
            </div>
          ) : (
            <div className="support-grant-banner support-grant-banner--active">
              Support access: {Array.from(grantedSections).join(', ')}
            </div>
          )
        )}

        <nav className="sidebar-nav">
          <div className="nav-section">Monitor</div>
          <NavItem to="/dashboard" label="Dashboard" />

          <RequirePermission perm="devices:read">
            {canAccess('devices')
              ? <NavItem to="/devices" label="Devices" />
              : <LockedNavItem label="Devices" />}
          </RequirePermission>

          <RequirePermission perm="fleets:read">
            {canAccess('fleets')
              ? <NavItem to="/fleets" label="Fleets" />
              : <LockedNavItem label="Fleets" />}
          </RequirePermission>

          <RequirePermission perm="devices:read">
            {canAccess('devices')
              ? <NavItem to="/events" label="Events" />
              : <LockedNavItem label="Events" />}
          </RequirePermission>

          <RequirePermission perm="devices:read">
            {canAccess('devices')
              ? <NavItem to="/twins" label="Digital Twins" />
              : <LockedNavItem label="Digital Twins" />}
          </RequirePermission>

          <RequirePermission perm="devices:read">
            {canAccess('devices')
              ? <NavItem to="/dashboards" label="Dashboards" />
              : <LockedNavItem label="Dashboards" />}
          </RequirePermission>

          <div className="nav-section">Deploy</div>

          <RequirePermission perm="operations:read">
            <NavItem to="/operations" label="Operations" />
          </RequirePermission>

          <RequirePermission perm="fleets:write">
            <NavItem to="/ota" label="OTA Updates" />
          </RequirePermission>

          <RequirePermission perm="fleets:write">
            <NavItem to="/playbooks" label="Playbooks" />
          </RequirePermission>

          <RequirePermission perm="fleets:write">
            <NavItem to="/scheduled-tasks" label="Scheduled Tasks" />
          </RequirePermission>

          <div className="nav-section">Configure</div>

          <RequirePermission perm="devices:write">
            {canAccess('credentials')
              ? <NavItem to="/credentials" label="SSH Credentials" />
              : <LockedNavItem label="SSH Credentials" />}
            <NavItem to="/device-types" label="Device Types" />
          </RequirePermission>

          <RequirePermission perm="devices:read">
            <NavItem to="/tenant-status" label="Tenant Status" />
          </RequirePermission>

          <RequirePermission perm="admin:access">
            <NavItem to="/notifications" label="Notifications" />
          </RequirePermission>

          <RequirePermission perm="admin:access">
            <NavItem to="/support-requests" label="Support Requests" />
          </RequirePermission>

          <RequirePermission perm="admin:access">
            <NavItem to="/admin" label="Admin" />
          </RequirePermission>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info" title="Click to sign out" onClick={() => auth.removeUser()}>
            <div className="user-avatar">{initial}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {username}
              </div>
              <div className="user-role">{roles[0] ?? 'viewer'}</div>
            </div>
            <button
              className="theme-toggle"
              onClick={e => { e.stopPropagation(); toggle() }}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="12" y1="2" x2="12" y2="5"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                  <line x1="2" y1="12" x2="5" y2="12"/>
                  <line x1="19" y1="12" x2="22" y2="12"/>
                  <line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/>
                  <line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/>
                  <line x1="19.78" y1="4.22" x2="17.66" y2="6.34"/>
                  <line x1="6.34" y1="17.66" x2="4.22" y2="19.78"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>
    </div>
  )
}
